// Core
import React, { Component } from 'react';
import CONSTANTS from 'chat-constants';
// Components
import { GECKO_CONSTS, GeckoRow, GeckoIcon, GeckoBtn, GeckoField } from 'ui';
import moment from 'moment';
import GeckoFeatures from 'services/gecko-features';
import AgentTeamSelect from 'components/AgentTeamSelect';
import BotSelect from 'components/BotSelect';
import TagSelect from 'components/TagSelect';
import ChannelSelect from 'components/ChannelSelect';
import DayRangePicker from 'components/DayRangePicker';
import TeamSelect from 'components/TeamSelect';

// Style
import './styles.scss';
import { GeckoToastService } from 'ui';
import { isSupportOrFeatureToggledOn } from '../../../../utilities/featureToggleHelper';

const PRESET_DATE_1_DAY = '1day';
const PRESET_DATE_7_DAYS = '7days';
const PRESET_DATE_4_WEEKS = '4weeks';
const PRESET_DATE_12_WEEKS = '12weeks';
const PRESET_DATE_CUSTOM = 'custom';

export default class FilterBar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      startDate: props.startDate,
      endDate: props.endDate,
      predefinedRange: props.predefinedDateRange || PRESET_DATE_7_DAYS,
      customDateRangeEnabled: props.customDateRangeEnabled || false,
      selectedBots: props.selectedBots || [],
      selectedChannels: props.selectedChannels || [],
      selectedRatings: props.selectedRatings || [],
      selectedAgents: props.selectedAgents || [],
      selectedTags: props.selectedTags || [],
    };

    // Binds
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handlePresetDateChange = this.handlePresetDateChange.bind(this);
    this.renderDatePicker = this.renderDatePicker.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.startDate !== prevProps.startDate) {
      this.setState({ startDate: this.props.startDate });
    }
    if (this.props.endDate !== prevProps.endDate) {
      this.setState({ endDate: this.props.endDate });
    }
  }

  handleDateChange(range) {
    this.setState({
      startDate: range.from ? moment(range.from).toDate() : null,
      endDate: range.to ? moment(range.to).toDate() : null,
    });
  }

  renderPredefinedDateRangePicker() {
    const field = {
      key: 'date',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: [
        {
          label: 'Today',
          value: PRESET_DATE_1_DAY,
        },
        {
          label: 'Past Week',
          value: PRESET_DATE_7_DAYS,
        },
        {
          label: 'Past 4 Weeks',
          value: PRESET_DATE_4_WEEKS,
        },
        {
          label: 'Past 12 Weeks',
          value: PRESET_DATE_12_WEEKS,
        },
        {
          label: 'Custom',
          value: PRESET_DATE_CUSTOM,
        },
      ],
      config: {
        optionRenderer: (opt) => this.renderPredefinedDateRangePickerItem(opt),
        valueRenderer: (opt) => this.renderPredefinedDateRangePickerItem(opt),
        clearable: false,
      },
    };
    return (
      <div className="date-range-preset-picker">
        <GeckoField
          field={field}
          values={{ date: this.state.predefinedRange }}
          handler={this.handlePresetDateChange.bind(this)}
        />
      </div>
    );
  }

  handlePresetDateChange(_key, value) {
    this.setState({ predefinedRange: value });
    if (value !== PRESET_DATE_CUSTOM) {
      let thisRange = this.getPresetDates(value);
      this.setState({
        customDateRangeEnabled: false,
        ...thisRange,
      });
    } else {
      this.setState({
        customDateRangeEnabled: true,
      });
    }
  }

  getPresetDates(value) {
    switch (value) {
      case PRESET_DATE_1_DAY:
        return {
          startDate: moment(),
          endDate: moment(),
        };
      case PRESET_DATE_7_DAYS:
        return {
          startDate: moment().subtract(7, 'days'),
          endDate: moment(),
        };
      case PRESET_DATE_4_WEEKS:
        return {
          startDate: moment().subtract(4, 'weeks'),
          endDate: moment(),
        };
      case PRESET_DATE_12_WEEKS:
        return {
          startDate: moment().subtract(12, 'weeks'),
          endDate: moment(),
        };

      default:
        return {
          startDate: moment().subtract(7, 'days'),
          endDate: moment(),
        };
    }
  }

  renderPredefinedDateRangePickerItem(item, i) {
    return (
      <span>
        <GeckoIcon icon="far fa-calendar-alt" />
        <span style={{ paddingLeft: '10px' }}>{item.label}</span>
      </span>
    );
  }

  renderDatePicker() {
    const value = {
      from: this.state.startDate ? moment(this.state.startDate).format() : null,
      to: this.state.endDate ? moment(this.state.endDate).format() : null,
    };
    const initialMonth = this.state.startDate
      ? moment(this.state.startDate).toDate()
      : moment(new Date()).subtract(1, 'months').toDate();
    return (
      <DayRangePicker
        value={value}
        onChange={this.handleDateChange.bind(this)}
        disableFuture={true}
        format=""
        displayFormat="Do MMM YYYY"
        dayPickerProps={{ initialMonth }}
      />
    );
  }

  renderTeamPicker() {
    return this.props.filterByTeams ? (
      <TeamSelect
        multi={false}
        value={this.state.selectedTeam}
        onChange={(team) => this.setState({ selectedTeam: team })}
        placeholder="Filter by Team"
      />
    ) : null;
  }
  renderAgentPicker() {
    return this.props.filterByAgents ? (
      <AgentTeamSelect
        value={this.state.selectedAgents}
        onChange={(agents) => this.setState({ selectedAgents: agents })}
        placeholder="Filter by Agents & Teams"
      />
    ) : null;
  }

  renderBotPicker() {
    return this.props.filterByBots && GeckoFeatures.can('chat-bot') ? (
      <BotSelect
        value={this.state.selectedBots}
        multi={true}
        onChange={(bots) => this.setState({ selectedBots: bots })}
        placeholder="Filter by Bots"
      />
    ) : null;
  }

  renderTagPicker() {
    return this.props.filterByTags ? (
      <TagSelect
        value={this.state.selectedTags}
        multi={true}
        onChange={(tags) => this.setState({ selectedTags: tags })}
        placeholder="Filter by Tags"
        showAITags={isSupportOrFeatureToggledOn(
          CONSTANTS.FEATURES.FEATURE_AI_INSIGHTS
        )}
      />
    ) : null;
  }

  renderChannelPicker() {
    return this.props.filterByChannels ? (
      <ChannelSelect
        includeDeletedChannels={true}
        value={this.state.selectedChannels}
        multi={true}
        onChange={(channels) => this.setState({ selectedChannels: channels })}
        placeholder="Filter by Channels"
      />
    ) : null;
  }

  renderRatingPicker() {
    if (!this.props.filterByRatings) return null;
    return (
      <GeckoField
        field={{
          key: 'rating',
          placeholder: 'Filter by Rating',
          type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
          options: [
            { label: GECKO_CONSTS.RATINGS_TYPE_LOVE, value: 5 },
            { label: GECKO_CONSTS.RATINGS_TYPE_HAPPY, value: 4 },
            { label: GECKO_CONSTS.RATINGS_TYPE_NEUTRAL, value: 3 },
            { label: GECKO_CONSTS.RATINGS_TYPE_SAD, value: 2 },
            { label: GECKO_CONSTS.RATINGS_TYPE_ANGRY, value: 1 },
          ],
        }}
        values={{ rating: this.state.selectedRatings }}
        handler={(key, value) => this.setState({ selectedRatings: value })}
      />
    );
  }

  renderFilterButton() {
    return (
      <GeckoBtn
        name="Apply Filter"
        icon="far fa-sliders-h"
        type={GECKO_CONSTS.BTN_TYPE_PRIMARY}
        handler={this.filter.bind(this)}
      />
    );
  }

  filter() {
    if (this.props.filter) {
      if (!this.state.startDate) {
        return new GeckoToastService().error(
          'You need to select a From date in the filter'
        );
      }
      if (!this.state.endDate) {
        return new GeckoToastService().error(
          'You need to select a To date in the filter'
        );
      }
      let digest = {
        predefinedRange: this.state.predefinedRange,
        customDateRangeEnabled: this.state.customDateRangeEnabled,
        startDate: this.state.startDate,
        endDate: this.state.endDate,
        filteredChannels: this.state.selectedChannels,
        filteredAgents: this.state.selectedAgents,
        filteredBots: this.state.selectedBots,
        filteredTags: this.state.selectedTags,
        filteredTeams: this.state.selectedTeam,
        filteredRatings: this.state.selectedRatings,
      };

      this.props.filter(digest);
    }
  }

  render() {
    return (
      <div className="statsFilterBar">
        <GeckoRow>
          <div className="col-12">
            <h1>Filter Stats</h1>
          </div>
        </GeckoRow>
        <GeckoRow>
          <div className="col d-flex justify-content-start">
            <div className="filterCell col">
              {this.renderPredefinedDateRangePicker()}
            </div>

            {this.state.customDateRangeEnabled ? (
              <div className="filterCell col">{this.renderDatePicker()}</div>
            ) : null}

            <div className="filterCell col">{this.renderTeamPicker()}</div>

            <div className="filterCell col">{this.renderAgentPicker()}</div>

            <div className="filterCell col">{this.renderBotPicker()}</div>

            <div className="filterCell col">{this.renderTagPicker()}</div>

            <div className="filterCell col">{this.renderChannelPicker()}</div>

            <div className="filterCell col">{this.renderRatingPicker()}</div>

            <div className="filterCell">{this.renderFilterButton()}</div>
          </div>
        </GeckoRow>
      </div>
    );
  }
}

FilterBar.propTypes = {};

FilterBar.defaultProps = {};
